/**
 * Coach-Panel — gemeinsame Chat-UI für den Coach-Tab (Vollbild) und das
 * Bottom-Sheet. Kontext-Badge (aktuelles Kapitel), Bubbles (assistant/user/
 * thinking/quota), Markdown-leichte Antwort, Mikrofon + Stop/Abort.
 */
import { Fragment } from "preact";
import { useEffect, useMemo, useRef } from "preact/hooks";
import { useLocation } from "preact-iso";
import {
  messages,
  context,
  busy,
  ask,
  stopCoach,
  pendingInsert,
  closeCoach,
  historySessions,
  historyHasOlder,
  pendingDeleteAll,
  pendingDeleteKey,
  sessionKey,
  resetHistoryPrompts,
  deleteHistorySession,
  deleteAllHistory,
} from "../state/coach";
import { isAuthenticated } from "../state/session";
import { activeCourseId } from "../data/course-store";
import { requestJump, parseJumpTarget } from "../state/course-nav";
import { showToast } from "../ui/toast";
import { createRecorder } from "./recorder";
import { formatAssistant } from "./format";
import { IconSpark, IconUser, IconMic, IconSend, IconClose } from "../app/icons";

interface Props {
  variant: "tab" | "sheet";
  onClose?: () => void;
  expanded?: boolean;
  onToggleExpanded?: () => void;
  rootRef?: import("preact").RefObject<HTMLDivElement>;
}

/** Lesbares Datum/Uhrzeit-Label einer vergangenen Sitzung. */
function sessionLabel(iso: string | null): string {
  if (!iso) return "Frühere Sitzung";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Frühere Sitzung";
  const datum = d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });
  const zeit = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  return `${datum} · ${zeit}`;
}

export function CoachPanel({ variant, onClose, expanded, onToggleExpanded, rootRef }: Props) {
  const { route } = useLocation();
  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const lastMessage = messages.value[messages.value.length - 1];

  /**
   * Kurs-Sprung-Links im Chat (1:1 zur Web-App, chatbot.js:593): delegierter
   * Klick auf `.chat-jump`. Wir vertrauen den gerenderten Attributen NICHT blind,
   * sondern bauen das Schema nach und validieren es erneut (parseJumpTarget).
   * Ablauf: zuerst Coach schließen, dann springen — entweder per Signal (man ist
   * schon auf /kurs, Course-Effect übernimmt) ODER per Route (sonst). Nie beides,
   * sonst doppelte History-Einträge.
   */
  function onLogClick(ev: MouseEvent) {
    const target = ev.target as Element | null;
    const link = target?.closest?.("a.chat-jump");
    if (!link) return;
    ev.preventDefault();
    const m = link.getAttribute("data-m") || "";
    const b = link.getAttribute("data-block") || "";
    const u = link.getAttribute("data-u") || "";
    const jump = parseJumpTarget(`modul.html?m=${m}${u ? `&u=${u}` : ""}#${b}`);
    if (!jump) {
      showToast("Dieser Link führt nicht zu einem Kapitel.");
      return;
    }
    closeCoach();
    const onCourse = typeof location !== "undefined" && location.pathname.startsWith("/kurs");
    if (onCourse) {
      requestJump(jump.m, jump.b, jump.u);
    } else {
      const courseId = activeCourseId.value;
      const uq = jump.u ? `&u=${encodeURIComponent(jump.u)}` : "";
      route(
        `/kurs?course=${encodeURIComponent(courseId)}&m=${encodeURIComponent(jump.m)}${uq}#${encodeURIComponent(jump.b)}`,
      );
    }
  }

  const recorder = useMemo(
    () =>
      createRecorder({
        onText: (text) => {
          const el = inputRef.current;
          if (!el) return;
          el.value = el.value.trim() ? `${el.value.trim()} ${text}` : text;
          el.focus();
        },
        onNotice: (text, quota) => {
          // Hinweise des Recorders als Coach-Nachricht zeigen.
          messages.value = [
            ...messages.value,
            { id: Date.now(), role: "assistant", content: text, kind: quota ? "quota" : "" },
          ];
        },
      }),
    [],
  );
  useEffect(() => () => recorder.dispose(), [recorder]);

  // „Frag die Kurs-KI"-Frage mit [Platzhaltern]: Text einsetzen, NICHT senden,
  // ersten Platzhalter markieren, damit der Nutzer ihn sofort überschreibt.
  useEffect(() => {
    const text = pendingInsert.value;
    const el = inputRef.current;
    if (!text || !el) return;
    el.value = text;
    pendingInsert.value = null;
    el.focus();
    const m = /\[[^\]]+\]/.exec(text);
    requestAnimationFrame(() => {
      if (m) el.setSelectionRange(m.index, m.index + m[0].length);
      else el.setSelectionRange(text.length, text.length);
    });
  }, [pendingInsert.value]);

  // Antworten am Anfang zeigen, Eingabe/Thinking weiter unten halten.
  useEffect(() => {
    const el = logRef.current;
    if (!el) return;
    const last = messages.value[messages.value.length - 1];
    requestAnimationFrame(() => {
      if (last?.role === "assistant" && last.kind !== "thinking") {
        // Nur LIVE-Nachrichten (direkte Kinder); History sitzt verschachtelt in
        // .coach-history und darf hier nicht als ":last-child" zuschlagen.
        const liveMsgs = el.querySelectorAll<HTMLElement>(":scope > .coach-msg");
        const lastEl = liveMsgs[liveMsgs.length - 1];
        if (!lastEl) return;
        const logRect = el.getBoundingClientRect();
        const msgRect = lastEl.getBoundingClientRect();
        const target = el.scrollTop + msgRect.top - logRect.top - 10;
        el.scrollTop = Math.max(0, target);
        return;
      }
      el.scrollTop = el.scrollHeight;
    });
  }, [lastMessage?.id, lastMessage?.kind, lastMessage?.role, messages.value.length, busy.value]);

  // Verlauf geladen/geaendert -> ans Ende scrollen (neueste Live-Nachricht sichtbar).
  useEffect(() => {
    const el = logRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [historySessions.value.length]);

  const ctx = context.value;
  const recState = recorder.state.value;

  function onSubmit(e: Event) {
    e.preventDefault();
    if (busy.value) {
      stopCoach();
      return;
    }
    const el = inputRef.current;
    if (!el) return;
    const text = el.value.trim();
    if (!text) return;
    el.value = "";
    void ask(text);
  }

  return (
    <div
      ref={rootRef}
      class={`coach coach--${variant}${expanded ? " expanded" : ""}`}
      role={variant === "sheet" ? "dialog" : "region"}
      aria-modal={variant === "sheet" ? "true" : undefined}
      aria-label="KI Coach"
    >
      {variant === "sheet" ? <div class="coach-grip" /> : null}

      <header class="coach-head">
        <div class="coach-title">
          <span class="coach-title-icon"><IconSpark size={18} /></span>
          <div>
            <strong>KI Coach</strong>
            <small>Sentinus Kursbegleiter</small>
          </div>
        </div>
        <div class="coach-head-actions">
          {variant === "sheet" && onToggleExpanded ? (
            <button
              type="button"
              class="coach-iconbtn"
              aria-label={expanded ? "Verkleinern" : "Maximieren"}
              title={expanded ? "Verkleinern" : "Maximieren"}
              onClick={onToggleExpanded}
            >
              {expanded ? "↙" : "↗"}
            </button>
          ) : null}
          {onClose ? (
            <button type="button" class="coach-iconbtn" aria-label="Schließen" onClick={onClose}>
              <IconClose size={18} />
            </button>
          ) : null}
        </div>
      </header>

      {ctx?.title ? (
        <div class="coach-context">
          <span class="dot" aria-hidden="true" />
          <span>Kontext: {ctx.title}</span>
        </div>
      ) : null}

      <div class="coach-log" ref={logRef} aria-live="polite" onClick={onLogClick}>
        {historySessions.value.length ? (
          <div class="coach-history">
            <div class="coach-history-bar">
              <span class="coach-history-hint">
                {historyHasOlder.value
                  ? "Ältere Gespräche? Frag den Coach einfach danach — oder stell deine Frage neu."
                  : "Frühere Gespräche"}
              </span>
              {pendingDeleteAll.value ? (
                <span class="coach-confirm">
                  Ganzen Verlauf löschen?{" "}
                  <button type="button" class="danger" onClick={() => void deleteAllHistory()}>
                    Endgültig löschen
                  </button>{" "}
                  <button type="button" onClick={resetHistoryPrompts}>
                    Abbrechen
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  class="coach-history-clear"
                  onClick={() => {
                    pendingDeleteKey.value = null;
                    pendingDeleteAll.value = true;
                  }}
                >
                  Verlauf löschen
                </button>
              )}
            </div>
            {historySessions.value.map((s) => {
              const key = sessionKey(s);
              return (
                <Fragment key={key}>
                  <div class="coach-session-sep">
                    <span class="coach-session-label">{sessionLabel(s.started_at)}</span>
                    {pendingDeleteKey.value === key ? (
                      <span class="coach-confirm">
                        Sitzung löschen?{" "}
                        <button
                          type="button"
                          class="danger"
                          onClick={() => void deleteHistorySession(s.from_id, s.to_id)}
                        >
                          Ja
                        </button>{" "}
                        <button type="button" onClick={resetHistoryPrompts}>
                          Nein
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        class="coach-session-del"
                        aria-label="Diese Sitzung löschen"
                        title="Diese Sitzung löschen"
                        onClick={() => {
                          pendingDeleteAll.value = false;
                          pendingDeleteKey.value = key;
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {s.messages.map((m) => (
                    <article key={`h-${m.id}`} class={`coach-msg ${m.role} is-history`}>
                      <span class="coach-avatar">
                        {m.role === "user" ? <IconUser size={18} /> : <IconSpark size={18} />}
                      </span>
                      <div class="coach-bubble">
                        {m.role === "assistant" ? formatAssistant(m.content) : <p>{m.content}</p>}
                      </div>
                    </article>
                  ))}
                </Fragment>
              );
            })}
          </div>
        ) : null}
        {messages.value.map((m) => (
          <article key={m.id} class={`coach-msg ${m.role}${m.kind ? " " + m.kind : ""}`}>
            <span class="coach-avatar">
              {m.role === "user" ? <IconUser size={18} /> : <IconSpark size={18} />}
            </span>
            <div class="coach-bubble">
              {m.kind === "thinking" ? (
                <p>
                  Ich denke nach
                  <span class="typing-dots" aria-hidden="true">
                    <i /><i /><i />
                  </span>
                </p>
              ) : m.role === "assistant" ? (
                formatAssistant(m.content)
              ) : (
                <p>{m.content}</p>
              )}
            </div>
          </article>
        ))}
      </div>

      <form class="coach-form" onSubmit={onSubmit}>
        <div class="coach-input-wrap">
          <textarea
            ref={inputRef}
            rows={2}
            maxLength={4000}
            placeholder="Frag mich etwas zum Kurs …"
            disabled={busy.value}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSubmit(e);
              }
            }}
          />
          <button
            type="button"
            class={`coach-record${recState === "recording" ? " recording" : ""}${
              recState === "transcribing" ? " transcribing" : ""
            }`}
            aria-label={recState === "recording" ? "Aufnahme stoppen" : "Spracheingabe starten"}
            title={
              recState === "recording"
                ? "Aufnahme stoppen (oder Sprechpause abwarten)"
                : "Spracheingabe starten"
            }
            disabled={recState === "transcribing" || busy.value}
            onClick={() => void recorder.toggle()}
          >
            <IconMic size={17} />
          </button>
          <button
            type="submit"
            class={`coach-send${busy.value ? " stop" : ""}`}
            aria-label={busy.value ? "Antwort abbrechen" : "Nachricht senden"}
            title={busy.value ? "Antwort abbrechen" : "Nachricht senden"}
          >
            {busy.value ? <span aria-hidden="true">■</span> : <IconSend size={17} />}
          </button>
        </div>
      </form>

      <p class="coach-note">
        {isAuthenticated.value
          ? "Nach dem Login nutze ich das aktuelle Modul/Kapitel als Kontext."
          : "Vor dem Login nur allgemeine Kursinfo, keine Kapiteltexte."}
      </p>
    </div>
  );
}
